// Grey placeholder blocks for the LIST or CARD area of a page while its rows load.
//
// Use this where the page chrome (header, filters) is already on screen and
// only the rows are waiting. A full-page / route load stays on <BrandLoader>
// (check-page-loader-rings). Pulses only when motion is allowed. Pass the
// loading branch only: keep any readState guard on figures as it was.
//
// SkeletonRows fades in after 120 ms, so a load that finishes inside that
// window never flashes grey. Skeleton alone does not: its pulse owns the
// `animation` property, and two animations cannot share it.
import clsx from 'clsx'

/** One grey block. Size and shape come from `className` (e.g. `h-12 rounded-xl`; no rounding by default).
 *  `bg-surface` follows the theme: on a `data-theme="light"` page it is the light grey. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={clsx('bg-surface motion-safe:animate-pulse', className)} />
}

/** `rows` row-shaped blocks for a list or table body, announced once as `label`. */
export function SkeletonRows({ rows = 5, label = 'Loading…', className, rowClassName = 'h-12 rounded-xl' }: {
  rows?:         number
  label?:        string
  className?:    string
  /** Shape of each row; match the real row's height (default `h-12 rounded-xl`). */
  rowClassName?: string
}) {
  return (
    // aria-label too: role=status takes no name from its content, so without it
    // the region is nameless (BrandLoader, which this replaces, says 'Loading').
    // The sr-only text stays for the live-region announcement.
    <div role="status" aria-label={label} className={clsx('space-y-2 motion-safe:animate-[dialog-in_150ms_ease-out_120ms_both]', className)}>
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={rowClassName} />
      ))}
    </div>
  )
}
